import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { 
  Building2, 
  Search, 
  Eye, 
  Check, 
  X, 
  Trash2, 
  Download, 
  RefreshCw,
  Users,
  Phone,
  MapPin,
  FileText,
  CreditCard,
  UserCheck,
  Edit,
  Save,
  KeyRound
} from 'lucide-react';
import { resolveFileUrl } from '../../utils/fileUrl';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const InstitutionalMembers = () => {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  
  // Edit states
  const [editingInstitution, setEditingInstitution] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [editConstitutionFile, setEditConstitutionFile] = useState(null);
  const [uploadingConstitution, setUploadingConstitution] = useState(false);
  
  // Reset password states
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  const fetchInstitutions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (searchTerm) params.search = searchTerm;

      const response = await axios.get(`${API_URL}/api/institutional-members`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setInstitutions(response.data.institutions || []);
    } catch (error) {
      console.error('Error fetching institutions:', error);
      toast.error('Failed to load institutions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/institutional-members/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchInstitutions();
    fetchStats();
  }, [filterStatus]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInstitutions();
  };

  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/institutional-members/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Institution ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchInstitutions();
      fetchStats();
      if (selectedInstitution?.id === id) {
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const deleteInstitution = async (id) => {
    if (!window.confirm('Are you sure you want to delete this institution?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/institutional-members/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Institution deleted successfully');
      fetchInstitutions();
      fetchStats();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error deleting institution:', error);
      toast.error('Failed to delete institution');
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResettingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: resetPasswordModal.email,
          userType: 'institutional',
          newPassword: newPassword
        })
      });
      if (response.ok) {
        toast.success(`Password reset successfully for ${resetPasswordModal.name}`);
        setResetPasswordModal(null);
        setNewPassword('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };


  const openEditModal = (institution) => {
    setEditingInstitution(institution);
    setEditConstitutionFile(null);
    setEditFormData({
      sansthaName: institution.sansthaName || '',
      address: institution.address || '',
      registrationNumber: institution.registrationNumber || '',
      numberOfFamilies: institution.numberOfFamilies || '',
      contactPhone: institution.contactPhone || '',
      sansthaEmail: institution.sansthaEmail || institution.contactEmail || '',
      officeBarers: institution.officeBarers || [],
      hasConstitution: institution.hasConstitution || '',
      otherInfo: institution.otherInfo || '',
      paymentAmount: institution.paymentAmount || '',
      paymentMode: institution.paymentMode || '',
      paymentDate: institution.paymentDate || '',
      transactionNumber: institution.transactionNumber || '',
      status: institution.status || 'pending'
    });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'hasConstitution' && value === 'no') {
      setEditConstitutionFile(null);
    }
  };

  const handleOfficeBearerChange = (index, field, value) => {
    setEditFormData(prev => {
      const updated = [...prev.officeBarers];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, officeBarers: updated };
    });
  };

  const addOfficeBearer = () => {
    setEditFormData(prev => ({
      ...prev,
      officeBarers: [...(prev.officeBarers || []), { designation: '', name: '', contact: '' }]
    }));
  };

  const removeOfficeBearer = (index) => {
    setEditFormData(prev => ({
      ...prev,
      officeBarers: prev.officeBarers.filter((_, i) => i !== index)
    }));
  };

  const saveInstitutionEdit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');

      // If there's a new constitution file, upload it first
      if (editConstitutionFile) {
        setUploadingConstitution(true);
        const fileData = new FormData();
        fileData.append('constitutionFile', editConstitutionFile);
        await axios.post(
          `${API_URL}/api/institutional-members/${editingInstitution.id}/upload-constitution`,
          fileData,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        setUploadingConstitution(false);
      }

      // Save the rest of the form data
      await axios.put(`${API_URL}/api/institutional-members/${editingInstitution.id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Institution updated successfully');
      setEditingInstitution(null);
      setEditConstitutionFile(null);
      fetchInstitutions();
      fetchStats();
    } catch (error) {
      setUploadingConstitution(false);
      toast.error(error.response?.data?.detail || 'Failed to update institution');
    } finally {
      setSaving(false);
    }
  };

  const exportToCSV = () => {
    if (institutions.length === 0) {
      toast.error('No data to export');
      return;
    }
    const rows = institutions.map(inst => {
      // Flatten office bearers into columns
      const bearerFields = {};
      (inst.officeBarers || []).forEach((ob, idx) => {
        const num = idx + 1;
        bearerFields[`Bearer ${num} Designation`] = ob.designation || '';
        bearerFields[`Bearer ${num} Name`] = ob.name || '';
        bearerFields[`Bearer ${num} Contact`] = ob.contact || '';
      });
      // Pad to at least 5 bearers
      for (let i = (inst.officeBarers || []).length + 1; i <= 5; i++) {
        bearerFields[`Bearer ${i} Designation`] = '';
        bearerFields[`Bearer ${i} Name`] = '';
        bearerFields[`Bearer ${i} Contact`] = '';
      }

      return {
        'Membership ID': inst.membershipId || inst.institutionId || '',
        'Institution Name': inst.sansthaName || inst.organizationName || '',
        'Organization Type': inst.organizationType || '',
        'Address': inst.address || '',
        'City': inst.city || '',
        'State': inst.state || '',
        'Pincode': inst.pincode || '',
        'Registration Number': inst.registrationNumber || '',
        'Establishment Year': inst.establishmentYear || '',
        'Number of Families': inst.numberOfFamilies || '',
        'Contact Phone': inst.contactPhone || '',
        'Institution Email': inst.sansthaEmail || '',
        'Contact Person Name': inst.contactPersonName || '',
        'Contact Person Phone': inst.contactPersonPhone || '',
        'Contact Person Email': inst.contactPersonEmail || '',
        'Login Email': inst.loginEmail || '',
        'Has Constitution': inst.hasConstitution || '',
        'Constitution File': inst.constitutionFile || '',
        ...bearerFields,
        'Payment Amount': inst.paymentAmount || '',
        'Payment Mode': inst.paymentMode || '',
        'Payment Date': inst.paymentDate || '',
        'Transaction Number': inst.transactionNumber || '',
        'Other Info': inst.otherInfo || '',
        'Status': inst.status || '',
        'Registration Date': inst.registrationDate ? new Date(inst.registrationDate).toLocaleDateString() : '',
        'Last Updated': inst.lastUpdated ? new Date(inst.lastUpdated).toLocaleDateString() : ''
      };
    });

    const headers = Object.keys(rows[0]);
    const csvContent = [
      headers.join(','),
      ...rows.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `institutional-members-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported successfully');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Institutional Members</h1>
          <p className="text-gray-600">Manage institutional membership registrations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchInstitutions(); fetchStats(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.approved}</p>
              <p className="text-sm text-gray-500">Approved</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <X className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.rejected}</p>
              <p className="text-sm text-gray-500">Rejected</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by name, address, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button type="submit">Search</Button>
        </form>
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'rejected'].map(status => (
            <Button
              key={status}
              variant={filterStatus === status ? 'default' : 'outline'}
              onClick={() => setFilterStatus(status)}
              size="sm"
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Institution ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Address</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Families</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Phone</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : institutions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No institutions found
                  </td>
                </tr>
              ) : (
                institutions.map(inst => (
                  <tr key={inst.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm">
                        {inst.institutionId || <span className="text-gray-400">Pending</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">{inst.sansthaName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {inst.address}
                    </td>
                    <td className="px-4 py-3">{inst.numberOfFamilies || '-'}</td>
                    <td className="px-4 py-3">{inst.contactPhone || '-'}</td>
                    <td className="px-4 py-3">{getStatusBadge(inst.status)}</td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(inst.registrationDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedInstitution(inst);
                            setShowDetailModal(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-orange-600 hover:text-orange-700"
                          onClick={() => openEditModal(inst)}
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {(inst.loginEmail || inst.sansthaEmail) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-600 hover:text-blue-800"
                            onClick={() => setResetPasswordModal({
                              email: inst.loginEmail || inst.sansthaEmail,
                              name: inst.sansthaName || 'Institution'
                            })}
                            title="Reset Password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                        )}
                        {inst.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => updateStatus(inst.id, 'approved')}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => updateStatus(inst.id, 'rejected')}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteInstitution(inst.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail Modal */}
      {showDetailModal && selectedInstitution && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold">{selectedInstitution.sansthaName}</h2>
                <div className="flex gap-2 mt-2">
                  {getStatusBadge(selectedInstitution.status)}
                  {selectedInstitution.institutionId && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-mono">
                      {selectedInstitution.institutionId}
                    </span>
                  )}
                </div>
              </div>
              <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> Address
                  </p>
                  <p className="font-medium">{selectedInstitution.address}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <FileText className="w-4 h-4" /> Registration Number
                  </p>
                  <p className="font-medium">{selectedInstitution.registrationNumber || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Users className="w-4 h-4" /> Number of Families
                  </p>
                  <p className="font-medium">{selectedInstitution.numberOfFamilies || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Phone className="w-4 h-4" /> Contact Phone
                  </p>
                  <p className="font-medium">{selectedInstitution.contactPhone || '-'}</p>
                </div>
                <div data-testid="detail-sanstha-email">
                  <p className="text-sm text-gray-500">Email ID of Sansthan</p>
                  <p className="font-medium">{selectedInstitution.sansthaEmail || selectedInstitution.contactEmail || '-'}</p>
                </div>
                <div data-testid="detail-login-email">
                  <p className="text-sm text-gray-500">Login Email</p>
                  <p className="font-medium">{selectedInstitution.loginEmail || '-'}</p>
                </div>
              </div>

              {/* Office Bearers */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <UserCheck className="w-5 h-5" /> Office Bearers
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {selectedInstitution.officeBarers?.map((bearer, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">{bearer.designation}</p>
                      <p className="font-medium">{bearer.name || '-'}</p>
                      {bearer.contact && <p className="text-sm text-gray-600">{bearer.contact}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Has Constitution?</p>
                  <p className="font-medium">{selectedInstitution.hasConstitution === 'yes' ? 'Yes / हाँ' : selectedInstitution.hasConstitution === 'no' ? 'No / नहीं' : '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Registration Date</p>
                  <p className="font-medium">
                    {new Date(selectedInstitution.registrationDate).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              {/* Constitution File */}
              {selectedInstitution.constitutionFile && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Constitution / By-laws Document
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={resolveFileUrl(selectedInstitution.constitutionFile)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      data-testid="view-constitution-file"
                    >
                      <Eye className="w-4 h-4" />
                      View Document
                    </a>
                    <a
                      href={resolveFileUrl(selectedInstitution.constitutionFile)}
                      download
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      data-testid="download-constitution-file"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </a>
                  </div>
                </div>
              )}

              {selectedInstitution.otherInfo && (
                <div>
                  <p className="text-sm text-gray-500">Other Information</p>
                  <p className="font-medium">{selectedInstitution.otherInfo}</p>
                </div>
              )}

              {/* Payment Details */}
              {(selectedInstitution.paymentAmount || selectedInstitution.paymentMode) && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" /> Payment Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-green-50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Amount</p>
                      <p className="font-medium">₹{selectedInstitution.paymentAmount || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Payment Mode</p>
                      <p className="font-medium">{selectedInstitution.paymentMode || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Payment Date</p>
                      <p className="font-medium">{selectedInstitution.paymentDate || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Transaction Number</p>
                      <p className="font-medium">{selectedInstitution.transactionNumber || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 border-t bg-gray-50 flex justify-between">
              <Button
                variant="destructive"
                onClick={() => deleteInstitution(selectedInstitution.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
              <div className="flex gap-2">
                {selectedInstitution.status === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => updateStatus(selectedInstitution.id, 'rejected')}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => updateStatus(selectedInstitution.id, 'approved')}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                  </>
                )}
                {selectedInstitution.status === 'approved' && (
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => updateStatus(selectedInstitution.id, 'rejected')}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Revoke Approval
                  </Button>
                )}
                {selectedInstitution.status === 'rejected' && (
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => updateStatus(selectedInstitution.id, 'approved')}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Institution Modal */}
      {editingInstitution && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingInstitution(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Edit className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="font-bold text-xl text-gray-900">Edit Institution</h2>
                  <p className="text-sm text-gray-500">संस्था संपादित करें</p>
                </div>
              </div>
              <button onClick={() => setEditingInstitution(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Institution Name / संस्था का नाम</Label>
                  <Input name="sansthaName" value={editFormData.sansthaName} onChange={handleEditChange} />
                </div>
                <div className="md:col-span-2">
                  <Label>Address / पता</Label>
                  <textarea
                    name="address"
                    value={editFormData.address}
                    onChange={handleEditChange}
                    rows={2}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                  />
                </div>
                <div>
                  <Label>Registration Number / पंजीकरण संख्या</Label>
                  <Input name="registrationNumber" value={editFormData.registrationNumber} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Number of Families / परिवारों की संख्या</Label>
                  <Input name="numberOfFamilies" type="number" value={editFormData.numberOfFamilies} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Contact Phone / संपर्क फ़ोन</Label>
                  <Input name="contactPhone" value={editFormData.contactPhone} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Contact Email / संपर्क ईमेल</Label>
                  <Input name="sansthaEmail" type="email" value={editFormData.sansthaEmail} onChange={handleEditChange} />
                </div>
                <div>
                  <Label>Has Constitution / संविधान है</Label>
                  <select name="hasConstitution" value={editFormData.hasConstitution} onChange={handleEditChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Select</option>
                    <option value="yes">Yes / हाँ</option>
                    <option value="no">No / नहीं</option>
                  </select>
                </div>
                {editFormData.hasConstitution === 'yes' && (
                  <div>
                    <Label>Constitution Document / संविधान दस्तावेज़</Label>
                    {editingInstitution?.constitutionFile && !editConstitutionFile && (
                      <div className="mb-2 flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-md">
                        <FileText className="w-4 h-4" />
                        <span>Document already uploaded</span>
                        <a
                          href={resolveFileUrl(editingInstitution.constitutionFile)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline ml-1"
                        >
                          View
                        </a>
                      </div>
                    )}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => setEditConstitutionFile(e.target.files[0] || null)}
                      className="w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                      data-testid="edit-constitution-file-input"
                    />
                    {editConstitutionFile && (
                      <p className="text-xs text-gray-500 mt-1">New file: {editConstitutionFile.name}</p>
                    )}
                    {!editingInstitution?.constitutionFile && !editConstitutionFile && (
                      <p className="text-xs text-orange-600 mt-1">Please upload the constitution document</p>
                    )}
                  </div>
                )}
                <div>
                  <Label>Status / स्थिति</Label>
                  <select name="status" value={editFormData.status} onChange={handleEditChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Office Bearers */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-700">Office Bearers / पदाधिकारी</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addOfficeBearer} data-testid="admin-add-bearer-btn">
                    + Add Bearer
                  </Button>
                </div>
                <div className="space-y-3">
                  {editFormData.officeBarers?.length === 0 && (
                    <p className="text-sm text-gray-400 italic">No office bearers added. Click "Add Bearer" to add.</p>
                  )}
                  {editFormData.officeBarers?.map((bearer, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 bg-gray-50 p-3 rounded-lg items-end">
                      <div>
                        <Label className="text-xs">Designation / पद</Label>
                        <Input value={bearer.designation || ''} onChange={(e) => handleOfficeBearerChange(idx, 'designation', e.target.value)} className="h-8 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Name / नाम</Label>
                        <Input value={bearer.name || ''} onChange={(e) => handleOfficeBearerChange(idx, 'name', e.target.value)} className="h-8 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Contact / संपर्क</Label>
                        <Input value={bearer.contact || ''} onChange={(e) => handleOfficeBearerChange(idx, 'contact', e.target.value)} className="h-8 text-sm" />
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-8 w-8 p-0" onClick={() => removeOfficeBearer(idx)} data-testid={`admin-remove-bearer-${idx}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Other Info */}
              <div>
                <Label>Other Information / अन्य जानकारी</Label>
                <textarea
                  name="otherInfo"
                  value={editFormData.otherInfo}
                  onChange={handleEditChange}
                  rows={2}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                />
              </div>

              {/* Payment Details */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Payment Details / भुगतान विवरण</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Amount / राशि (₹)</Label>
                    <Input name="paymentAmount" value={editFormData.paymentAmount} onChange={handleEditChange} />
                  </div>
                  <div>
                    <Label>Payment Mode / भुगतान माध्यम</Label>
                    <select name="paymentMode" value={editFormData.paymentMode} onChange={handleEditChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                      <option value="">Select</option>
                      <option value="Online">Online</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                    </select>
                  </div>
                  <div>
                    <Label>Payment Date / भुगतान तिथि</Label>
                    <Input name="paymentDate" type="date" value={editFormData.paymentDate} onChange={handleEditChange} />
                  </div>
                  <div>
                    <Label>Transaction Number / लेन-देन संख्या</Label>
                    <Input name="transactionNumber" value={editFormData.transactionNumber} onChange={handleEditChange} />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditingInstitution(null)}>
                Cancel
              </Button>
              <Button onClick={saveInstitutionEdit} disabled={saving || uploadingConstitution} className="bg-orange-600 hover:bg-orange-700">
                {uploadingConstitution ? 'Uploading document...' : saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <KeyRound className="w-6 h-6 text-blue-600" />
              <h3 className="text-lg font-bold">Reset Password</h3>
            </div>
            <div className="mb-4">
              <p className="font-semibold text-gray-900">{resetPasswordModal.name}</p>
              <p className="text-sm text-gray-500">{resetPasswordModal.email}</p>
            </div>
            <form onSubmit={resetPassword} className="space-y-4">
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                  data-testid="inst-reset-password-input"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => { setResetPasswordModal(null); setNewPassword(''); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={resettingPassword} data-testid="inst-reset-password-submit">
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstitutionalMembers;
